import React, { useState, useEffect, useCallback, useMemo } from 'react';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { EmployeeSelect } from '../components/EmployeeAutocomplete';
import { currentFY } from '../lib/fyUtils';
import {
  FileBarChart, Download, FileSpreadsheet, FileText,
  Search, Filter, ChevronDown, Loader2, Settings2,
  Save, RotateCcw, SlidersHorizontal, AlertTriangle,
  ArrowRightLeft, Layers, ShieldCheck, Eye, HelpCircle,
  GripVertical, ArrowUp, ArrowDown, ArrowRight, Trash2, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_COLORS = {
  Payroll: 'bg-blue-50 text-blue-700 border-blue-200',
  Attendance: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Leave: 'bg-amber-50 text-amber-700 border-amber-200',
  Claims: 'bg-purple-50 text-purple-700 border-purple-200',
  Separation: 'bg-red-50 text-red-700 border-red-200',
  Compliance: 'bg-slate-50 text-slate-700 border-slate-200',
  'Statutory & Compliance': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Operational Registers': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Reconciliation Reports': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Management / MIS Reports': 'bg-violet-50 text-violet-700 border-violet-200',
  'Comparison & Variance': 'bg-amber-50 text-amber-700 border-amber-200',
};

const ReportsCenter = () => {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [fieldRegistry, setFieldRegistry] = useState({});
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [selectedEmployee, setSelectedEmployee] = useState('');

  // Statutory Benefit Filters
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [branches, setBranches] = useState([]);
  const [benefitTypeFilter, setBenefitTypeFilter] = useState('all');
  const [frequencyFilter, setFrequencyFilter] = useState('overall');
  const [financialYearFilter, setFinancialYearFilter] = useState('2026-27');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodLockStatusFilter, setPeriodLockStatusFilter] = useState('all');
  const [payrollSourceFilter, setPayrollSourceFilter] = useState('both');

  // Reset dependent filters when company changes to prevent stale data
  useEffect(() => {
    setSelectedBranch('all');
    setSelectedDepartment('all');
    setSelectedEmployee('');
  }, [selectedCompany]);

  // Period State
  const [periodType, setPeriodType] = useState('month');
  const [period, setPeriod] = useState('');
  const [period2, setPeriod2] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [processedPeriods, setProcessedPeriods] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Preview & Modal State
  const [previewData, setPreviewData] = useState(null);
  const [previewReport, setPreviewReport] = useState(null);
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownModal, setDrilldownModal] = useState(false);

  // Reusable Customization Panel & Saved Views State
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [activeColumns, setActiveColumns] = useState([]);
  const [columnAliases, setColumnAliases] = useState({});
  const [savedViews, setSavedViews] = useState([]);
  const [viewNameInput, setViewNameInput] = useState('');
  const [isSharedView, setIsSharedView] = useState(false);
  const [isDefaultView, setIsDefaultView] = useState(false);

  // Two-Panel Column Chooser State
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchSelected, setSearchSelected] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);

  const REQUIRED_COLUMNS = useMemo(() => ['employee_code', 'employee_name'], []);

  // Comparison & Variance State
  const [comparisonData, setComparisonData] = useState(null);
  const [reconcileData, setReconcileData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [v1Catalog, v2Catalog, compRes] = await Promise.all([
        axios.get(`${API_URL}/reports/catalog`, { withCredentials: true }),
        axios.get(`${API_URL}/reports/v2/catalog`, { withCredentials: true }),
        axios.get(`${API_URL}/companies`, { withCredentials: true }),
      ]);

      const v1Data = v1Catalog.data || [];
      const v2Data = v2Catalog.data?.catalog || [];
      const registry = v2Catalog.data?.field_registry || {};

      // Merge catalogs cleanly
      const mergedMap = new Map();
      [...v1Data, ...v2Data].forEach((item) => {
        if (!mergedMap.has(item.id)) {
          mergedMap.set(item.id, item);
        }
      });

      setCatalog(Array.from(mergedMap.values()));
      setFieldRegistry(registry);
      setCompanies(compRes.data);
      setSelectedCompany((cur) => (compRes.data.length > 0 && !cur ? compRes.data[0].id : cur));
    } catch (err) {
      console.error('Failed to load report catalog:', err);
    } finally {
      setLoading(false);
    }
  }, [setSelectedCompany]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchProcessedPeriods = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/reports/processed-periods?company_id=${selectedCompany}`, { withCredentials: true });
      setProcessedPeriods(data || []);
      if (data?.length) {
        const firstWithData = data.find((p) => p.has_data) || data[0];
        if (!period || !data.some((p) => (p.period || p.period_name) === period)) {
          setPeriod(firstWithData.period || firstWithData.period_name);
        }
      }
    } catch (err) {
      console.error('processed-periods fetch failed', err);
      setProcessedPeriods([]);
    }
  }, [period, selectedCompany]);

  useEffect(() => {
    if (selectedCompany) fetchProcessedPeriods();
  }, [selectedCompany, fetchProcessedPeriods]);

  const fetchBranches = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/masters/locations?company_id=${selectedCompany}`, { withCredentials: true });
      setBranches(data || []);
    } catch (err) {
      console.error('Failed to load branches:', err);
      setBranches([]);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany) fetchBranches();
  }, [selectedCompany, fetchBranches]);

  const selectedPeriodInfo = processedPeriods.find((p) => (p.period || p.period_name) === period);

  // Load Saved Views for Active Report
  const loadSavedViews = async (reportId) => {
    try {
      const { data } = await axios.get(`${API_URL}/reports/v2/custom-views?report_id=${reportId}`, { withCredentials: true });
      setSavedViews(data || []);
    } catch (err) {
      console.error('Failed to load saved views:', err);
      setSavedViews([]);
    }
  };

  const handlePreview = async (report) => {
    if (periodFrom && periodTo && periodFrom > periodTo) {
      toast.error('Period From cannot be later than Period To');
      return;
    }

    setPreviewReport(report);
    setPreviewData(null);
    setComparisonData(null);
    setReconcileData(null);

    // Initialize Active Columns from Default Or Field Registry
    const defaults = report.default_columns || ['employee_code', 'employee_name', 'department', 'gross_pay', 'total_deductions', 'net_pay'];
    setActiveColumns(defaults);

    loadSavedViews(report.id);

    try {
      if (report.id === 'legacy_vs_regular_comparison') {
        const { data } = await axios.get(`${API_URL}/reports/v2/legacy-vs-regular-comparison`, {
          params: {
            company_id: selectedCompany,
            branch_id: selectedBranch,
            financial_year: financialYearFilter,
            payroll_period: period,
            payroll_source: payrollSourceFilter,
            department: selectedDepartment,
            status: statusFilter,
          },
          withCredentials: true,
        });
        setComparisonData(null);
        setReconcileData(null);
        setPreviewData({
          data: data.data || data.comparison_rows || [],
          count: (data.data || data.comparison_rows || []).length,
          period: data.reporting_period || period,
          summary: data.summary,
          company_name: data.company_name,
          branch_name: data.branch_name,
          financial_year: data.financial_year,
          payroll_period: data.reporting_period || period,
        });
        return;
      }

      if (report.id === 'period_comparison_variance' || report.id === 'variance_bridge_waterfall' || report.id === 'payroll-variance') {
        const p2 = period2 || (processedPeriods.length > 1 ? processedPeriods[1].period || processedPeriods[1].period_name : period);
        const { data } = await axios.get(`${API_URL}/reports/v2/comparison`, {
          params: { company_id: selectedCompany, branch_id: selectedBranch, period_1: period, period_2: p2 },
          withCredentials: true,
        });
        setComparisonData(data);
        setPreviewData({
          data: data.comparison_rows || [],
          count: (data.comparison_rows || []).length,
          period,
          period_labels: data.period_labels,
          periods: data.periods,
          company_name: data.company_name,
          branch_name: data.branch_name,
        });
        return;
      }

      if (report.id === 'payroll_vs_register_reconciliation') {
        const { data } = await axios.get(`${API_URL}/reports/v2/reconciliation`, {
          params: { company_id: selectedCompany, period },
          withCredentials: true,
        });
        setReconcileData(data);
        setPreviewData({ data: [data], count: 1, period });
        return;
      }

      if (['gratuity-report', 'gratuity_report', 'bonus-report', 'bonus_report', 'leave-encashment-report', 'leave_encashment_report', 'combined-statutory-benefits-report', 'combined_statutory_benefits_report'].includes(report.id)) {
        const bType = report.id.includes('gratuity') ? 'gratuity' : (report.id.includes('bonus') ? 'bonus' : (report.id.includes('leave') ? 'leave_encashment' : benefitTypeFilter));
        const { data } = await axios.get(`${API_URL}/reports/statutory-benefits`, {
          params: {
            company_id: selectedCompany,
            branch_id: selectedBranch,
            benefit_type: bType,
            frequency: frequencyFilter,
            financial_year: financialYearFilter,
            payroll_period: period,
            period_from: periodFrom || period,
            period_to: periodTo,
            department: selectedDepartment,
            employee_id: selectedEmployee,
            status: statusFilter,
            period_lock_status: periodLockStatusFilter,
          },
          withCredentials: true,
        });
        setPreviewData(data);
        return;
      }

      // V2 Standard Reports Query with ALL filters passed!
      const { data } = await axios.get(`${API_URL}/reports/v2/data/${report.id}`, {
        params: {
          company_id: selectedCompany,
          branch_id: selectedBranch,
          period_type: periodType,
          period_from: periodFrom || period,
          period_to: periodTo,
          frequency: frequencyFilter,
          benefit_type: benefitTypeFilter,
          financial_year: financialYearFilter,
          status: statusFilter,
          period_lock_status: periodLockStatusFilter,
          department: selectedDepartment,
          employee_id: selectedEmployee,
        },
        withCredentials: true,
      });
      setPreviewData(data);
    } catch (err) {
      console.error('Preview failed:', err);
      setPreviewData({ error: 'Failed to load preview' });
    }
  };

  const handleDrilldown = async (dept = '') => {
    try {
      const { data } = await axios.get(`${API_URL}/reports/v2/drilldown`, {
        params: { company_id: selectedCompany, period, department: dept },
        withCredentials: true,
      });
      setDrilldownData(data);
      setDrilldownModal(true);
    } catch (err) {
      toast.error('Failed to load drill-down records');
    }
  };

  const handleSaveView = async () => {
    if (!viewNameInput.trim() || !previewReport) return;
    try {
      await axios.post(`${API_URL}/reports/v2/custom-views`, {
        report_id: previewReport.id,
        view_name: viewNameInput.trim(),
        selected_columns: activeColumns,
        column_aliases: columnAliases,
        is_shared: isSharedView,
        is_default: isDefaultView,
      }, { withCredentials: true });
      toast.success('Custom Report View saved!');
      setViewNameInput('');
      loadSavedViews(previewReport.id);
    } catch (err) {
      toast.error('Failed to save custom view');
    }
  };

  const applySavedView = (view) => {
    if (view.selected_columns?.length) setActiveColumns(view.selected_columns);
    if (view.column_aliases) setColumnAliases(view.column_aliases);
    toast.success(`Applied view "${view.view_name}"`);
  };

  const resetToStandardView = () => {
    if (!previewReport) return;
    const defaults = previewReport.default_columns || ['employee_code', 'employee_name', 'department', 'gross_pay', 'total_deductions', 'net_pay'];
    setActiveColumns(defaults);
    setColumnAliases({});
    toast.success('Reset layout to system standard');
  };

  // Two-Panel Column Selector Helper Computations
  const allAvailableKeys = useMemo(() => {
    if (!previewReport) return [];
    const regKeys = Object.keys(fieldRegistry || {});
    if (regKeys.length > 0) return regKeys;
    return [
      'company', 'branch', 'employee_code', 'employee_name', 'department', 'designation',
      'date_of_joining', 'date_of_exit', 'benefit_type', 'eligible', 'frequency', 'calculation_mode',
      'eligibility_from', 'eligibility_to', 'calculation_period_from', 'calculation_period_to',
      'financial_year', 'payroll_period', 'eligible_days', 'eligible_months', 'basic',
      'eligible_wage', 'applicable_rate', 'wage_ceiling', 'calculated_amount', 'manual_adjustment',
      'final_amount', 'source', 'calculation_run_id', 'status', 'period_lock_status',
      'calculated_date', 'calculated_by', 'approved_date', 'approved_by', 'payment_responsibility'
    ];
  }, [previewReport, fieldRegistry]);

  const availableUnselectedKeys = useMemo(() => {
    return allAvailableKeys.filter((k) => !activeColumns.includes(k));
  }, [allAvailableKeys, activeColumns]);

  const filteredAvailable = useMemo(() => {
    if (!searchAvailable.trim()) return availableUnselectedKeys;
    const q = searchAvailable.toLowerCase();
    return availableUnselectedKeys.filter((k) => {
      const f = (fieldRegistry || {})[k] || {};
      const lbl = f.label || k;
      return lbl.toLowerCase().includes(q) || k.toLowerCase().includes(q);
    });
  }, [availableUnselectedKeys, searchAvailable, fieldRegistry]);

  const filteredSelected = useMemo(() => {
    if (!searchSelected.trim()) return activeColumns;
    const q = searchSelected.toLowerCase();
    return activeColumns.filter((k) => {
      const f = (fieldRegistry || {})[k] || {};
      const lbl = f.label || k;
      const alias = columnAliases[k] || '';
      return lbl.toLowerCase().includes(q) || k.toLowerCase().includes(q) || alias.toLowerCase().includes(q);
    });
  }, [activeColumns, searchSelected, fieldRegistry, columnAliases]);

  const addColumnToSelected = (colKey) => {
    if (!activeColumns.includes(colKey)) {
      setActiveColumns([...activeColumns, colKey]);
    }
  };

  const removeColumnFromSelected = (colKey) => {
    if (REQUIRED_COLUMNS.includes(colKey)) {
      toast.error(`Column "${colKey}" is required and cannot be removed.`);
      return;
    }
    setActiveColumns(activeColumns.filter((k) => k !== colKey));
  };

  const moveColumn = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= activeColumns.length) return;
    const updated = [...activeColumns];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setActiveColumns(updated);
  };

  const handleDrop = (dropIndex) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    moveColumn(draggedIndex, dropIndex);
    setDraggedIndex(null);
  };

  const handleSelectAllColumns = () => {
    const combined = [...activeColumns];
    availableUnselectedKeys.forEach((k) => {
      if (!combined.includes(k)) combined.push(k);
    });
    setActiveColumns(combined);
  };

  const handleClearAllColumns = () => {
    const reqOnly = activeColumns.filter((k) => REQUIRED_COLUMNS.includes(k));
    setActiveColumns(reqOnly.length > 0 ? reqOnly : ['employee_code', 'employee_name']);
  };

  const handleDownload = async (report, fmt) => {
    if (downloading) return;
    if (periodFrom && periodTo && periodFrom > periodTo) {
      toast.error('Period From cannot be later than Period To');
      return;
    }

    setDownloading(`${report.id}-${fmt}`);
    try {
      let response;
      const rId = report.id;

      if (rId === 'permission-register' || rId === 'permission_register') {
        window.open(`${API_URL}/leave/permissions/export`, '_blank');
        setDownloading(null);
        return;
      } else if (rId === 'form16-pdf' || rId === 'form16') {
        if (selectedEmployee) {
          response = await axios.get(`${API_URL}/reports/form16/pdf`, {
            params: { company_id: selectedCompany, employee_id: selectedEmployee, financial_year: financialYearFilter || currentFY() },
            withCredentials: true,
            responseType: 'blob',
          });
        } else {
          response = await axios.get(`${API_URL}/reports/form16/bulk-zip`, {
            params: { company_id: selectedCompany, financial_year: financialYearFilter || currentFY() },
            withCredentials: true,
            responseType: 'blob',
          });
        }
      } else if (rId === 'form-12bb-pdf' || rId === 'form12bb') {
        response = await axios.get(`${API_URL}/reports/form-12bb/pdf`, {
          params: { company_id: selectedCompany, employee_id: selectedEmployee || '', financial_year: financialYearFilter || currentFY() },
          withCredentials: true,
          responseType: 'blob',
        });
      } else if (rId === 'form-24q-pdf' || rId === 'form24q') {
        response = await axios.get(`${API_URL}/reports/form-24q/pdf`, {
          params: { company_id: selectedCompany, quarter: periodType === 'quarter' ? period : 'Q1', financial_year: financialYearFilter || currentFY() },
          withCredentials: true,
          responseType: 'blob',
        });
      } else {
        const p2 = period2 || (processedPeriods.length > 1 ? processedPeriods[1].period || processedPeriods[1].period_name : period);
        // Dynamic export via Enterprise v2 POST export pipeline using canonical dataset and active UI filters
        response = await axios.post(`${API_URL}/reports/v2/export`, {
          report_id: rId,
          company_id: selectedCompany,
          branch_id: selectedBranch,
          period_type: periodType,
          period_from: periodFrom || period,
          period_to: periodTo,
          period: period,
          period_1: period,
          period_2: p2,
          payroll_source: payrollSourceFilter,
          search: searchTerm,
          frequency: frequencyFilter,
          benefit_type: benefitTypeFilter,
          financial_year: financialYearFilter,
          status: statusFilter,
          period_lock_status: periodLockStatusFilter,
          department: selectedDepartment,
          employee_id: selectedEmployee,
          format: fmt,
          selected_columns: activeColumns,
          column_aliases: columnAliases,
        }, {
          withCredentials: true,
          responseType: 'blob',
        });
      }

      // Check if response is a JSON error payload disguised as a blob
      if (response.data && response.data.type === 'application/json') {
        try {
          const text = await response.data.text();
          const parsed = JSON.parse(text);
          if (parsed && (parsed.detail || parsed.message)) {
            toast.error(parsed.detail || parsed.message);
            return;
          }
        } catch (_) {}
      }

      if (!response.data || response.data.size === 0) {
        toast.error('Generated export file is empty');
        return;
      }

      // Extract filename from header or default fallback
      let filename = `${report.id}_${period || 'report'}.${fmt === 'excel' ? 'xlsx' : fmt === 'csv' ? 'csv' : 'pdf'}`;
      const disposition = response.headers['content-disposition'];
      if (disposition) {
        const filenameMatch = disposition.match(/filename=["']?([^"';]+)["']?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()} report successfully`);
    } catch (err) {
      let errMsg = 'Report export failed';
      if (err.response && err.response.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed && parsed.detail) {
            errMsg = parsed.detail;
          }
        } catch (_) {}
      } else if (err.message) {
        errMsg = err.message;
      }
      toast.error(errMsg);
    } finally {
      setDownloading(null);
    }
  };

  const categories = ['all', ...new Set(catalog.map((r) => r.category))];
  const filtered = catalog.filter((r) => {
    if (filterCategory !== 'all' && r.category !== filterCategory) return false;
    if (searchTerm && !r.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="reports-center-page">
        {/* Top Title Bar */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Enterprise Reports Center</h1>
            <p className="mt-1 text-sm text-slate-500">
              Read-Only Reporting Layer · {catalog.length} Enterprise Reports &amp; Custom Views
            </p>
          </div>
          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-700">
            <ShieldCheck className="w-4 h-4 text-blue-600" />
            <span>Read-Only Single Source of Truth</span>
          </div>
        </div>

        {/* Global Controls & Period Bar */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm space-y-4" data-testid="reports-controls">
          <div className="flex flex-wrap gap-4 items-end">
            {user?.role === 'super_admin' && (
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs font-medium text-slate-500 mb-1">Company / Entity</label>
                <select
                  data-testid="reports-company-select"
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Branch Selector */}
            <div className="min-w-[160px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Branch</label>
              <select
                data-testid="reports-branch-select"
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Branches</option>
                <option value="main_company">Main Company Only</option>
                {branches.map((b) => (
                  <option key={b.id || b._id} value={b.id || b._id}>{b.name || b.branch_name}</option>
                ))}
              </select>
            </div>

            {/* Benefit Type Filter */}
            <div className="min-w-[180px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Benefit Type</label>
              <select
                data-testid="reports-benefit-type-select"
                value={benefitTypeFilter}
                onChange={(e) => setBenefitTypeFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-medium text-slate-800"
              >
                <option value="all">All Statutory Benefits</option>
                <option value="gratuity">Gratuity</option>
                <option value="bonus">Statutory Bonus</option>
                <option value="leave_encashment">Leave Encashment</option>
              </select>
            </div>

            {/* Frequency Filter */}
            <div className="min-w-[150px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Frequency</label>
              <select
                data-testid="reports-frequency-select"
                value={frequencyFilter}
                onChange={(e) => setFrequencyFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-semibold text-blue-700 bg-blue-50/50"
              >
                <option value="overall">Overall (All)</option>
                <option value="monthly">Monthly Only</option>
                <option value="annual">Annual Only</option>
              </select>
            </div>

            {/* Financial Year Selector */}
            <div className={`min-w-[150px] ${frequencyFilter === 'monthly' ? 'opacity-70' : 'ring-1 ring-purple-300 rounded-md'}`}>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Financial Year {frequencyFilter === 'annual' && <span className="text-purple-600 font-bold">(Active)</span>}
              </label>
              <select
                data-testid="reports-fy-select"
                value={financialYearFilter}
                onChange={(e) => setFinancialYearFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-medium"
              >
                <option value="2024-25">FY 2024-25</option>
                <option value="2025-26">FY 2025-26</option>
                <option value="2026-27">FY 2026-27</option>
                <option value="2027-28">FY 2027-28</option>
              </select>
            </div>

            {/* Primary Period Select */}
            <div className={`min-w-[220px] flex-1 ${frequencyFilter === 'annual' ? 'opacity-70' : 'ring-1 ring-blue-300 rounded-md'}`}>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Payroll Period {frequencyFilter === 'monthly' && <span className="text-blue-600 font-bold">(Active)</span>}
              </label>
              <select
                data-testid="reports-period-select"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {processedPeriods.length === 0 && <option value="">No processed periods yet</option>}
                {processedPeriods.map((p) => {
                  const key = p.period || p.period_name;
                  return (
                    <option key={p.period_id} value={key}>
                      {`✓ ${key} · ${p.details_count} emp · Gross ₹${(p.total_gross || 0).toLocaleString('en-IN')}`}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Comparison Period (Period 2) */}
            <div className="min-w-[170px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Comparison Period (P2)</label>
              <select
                data-testid="reports-period-2-select"
                value={period2}
                onChange={(e) => setPeriod2(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-medium"
              >
                <option value="">Auto (Previous Period)</option>
                {processedPeriods.map((p) => {
                  const key = p.period || p.period_name;
                  return (
                    <option key={p.period_id || key} value={key}>
                      {key}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Payroll Source Selector */}
            <div className="min-w-[170px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Payroll Source</label>
              <select
                data-testid="reports-payroll-source-select"
                value={payrollSourceFilter}
                onChange={(e) => setPayrollSourceFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-medium"
              >
                <option value="both">Compare Both (Legacy &amp; Regular)</option>
                <option value="regular">Regular Payroll Only</option>
                <option value="legacy">Legacy Payroll Only</option>
              </select>
            </div>

            {/* Period From */}
            <div className="min-w-[140px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Period From</label>
              <input
                data-testid="reports-period-from-input"
                type="month"
                value={periodFrom}
                onChange={(e) => setPeriodFrom(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-medium text-slate-800"
              />
            </div>

            {/* Period To */}
            <div className="min-w-[140px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Period To</label>
              <input
                data-testid="reports-period-to-input"
                type="month"
                value={periodTo}
                onChange={(e) => setPeriodTo(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 font-medium text-slate-800"
              />
            </div>

            {/* Status Filter */}
            <div className="min-w-[140px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
              <select
                data-testid="reports-status-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="calculated">Calculated</option>
                <option value="approved">Approved</option>
                <option value="locked">Locked</option>
              </select>
            </div>

            {/* Search Box */}
            <div className="min-w-[200px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Search Reports</label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  data-testid="reports-search-input"
                  type="text"
                  placeholder="Type report name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2" data-testid="reports-category-tabs">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`reports-tab-${cat}`}
              onClick={() => setFilterCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                filterCategory === cat
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
            >
              {cat === 'all' ? 'All Reports' : cat} {cat !== 'all' ? `(${catalog.filter((r) => r.category === cat).length})` : ''}
            </button>
          ))}
        </div>

        {/* Report Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reports-grid">
          {filtered.map((report) => (
            <div
              key={report.id}
              data-testid={`report-card-${report.id}`}
              className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">{report.name}</h3>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-semibold border ${CATEGORY_COLORS[report.category] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                      {report.category}
                    </span>
                  </div>
                  <FileBarChart className="w-5 h-5 text-slate-300" />
                </div>
                <p className="text-xs text-slate-500 mb-4">{report.description || 'Enterprise report reading finalized payroll result data.'}</p>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-4">
                <button
                  onClick={() => handlePreview(report)}
                  data-testid={`preview-btn-${report.id}`}
                  className="flex-1 min-w-[120px] flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition-colors"
                >
                  <Search className="w-3.5 h-3.5" /> Preview &amp; Customize
                </button>
                <button
                  onClick={() => handleDownload(report, 'excel')}
                  disabled={Boolean(downloading)}
                  data-testid={`export-excel-${report.id}`}
                  className="flex items-center justify-center gap-1 px-2.5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors"
                  title="Export Excel"
                >
                  {downloading === `${report.id}-excel` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
                  <span>Excel</span>
                </button>
                <button
                  onClick={() => handleDownload(report, 'csv')}
                  disabled={Boolean(downloading)}
                  data-testid={`export-csv-${report.id}`}
                  className="flex items-center justify-center gap-1 px-2.5 py-2 bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors"
                  title="Export CSV"
                >
                  {downloading === `${report.id}-csv` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                  <span>CSV</span>
                </button>
                <button
                  onClick={() => handleDownload(report, 'pdf')}
                  disabled={Boolean(downloading)}
                  data-testid={`export-pdf-${report.id}`}
                  className="flex items-center justify-center gap-1 px-2.5 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition-colors"
                  title="Export PDF"
                >
                  {downloading === `${report.id}-pdf` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  <span>PDF</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Dynamic Report Preview & Customization Panel Modal */}
        {previewReport && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="report-preview-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 px-6 border-b border-slate-200 bg-slate-50">
                <div>
                  <h2 className="font-bold text-slate-900 text-lg flex items-center gap-2">
                    <FileBarChart className="w-5 h-5 text-blue-600" />
                    {previewReport.name}
                  </h2>
                  <p className="text-xs text-slate-500">
                    Period: <strong className="text-slate-800">{period || 'Current'}</strong> · Entity: <strong className="text-slate-800">{selectedCompany}</strong>
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowCustomizer(!showCustomizer)}
                    data-testid="customize-columns-btn"
                    className="px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:bg-blue-100"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" /> Customize Columns
                  </button>
                  <button
                    onClick={() => handleDownload(previewReport, 'excel')}
                    disabled={Boolean(downloading)}
                    data-testid="modal-export-excel-btn"
                    className="px-3 py-1.5 bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:bg-emerald-700"
                  >
                    {downloading === `${previewReport.id}-excel` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Export Excel
                  </button>
                  <button
                    onClick={() => handleDownload(previewReport, 'csv')}
                    disabled={Boolean(downloading)}
                    data-testid="modal-export-csv-btn"
                    className="px-3 py-1.5 bg-slate-700 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-800"
                  >
                    {downloading === `${previewReport.id}-csv` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Export CSV
                  </button>
                  <button
                    onClick={() => handleDownload(previewReport, 'pdf')}
                    disabled={Boolean(downloading)}
                    data-testid="modal-export-pdf-btn"
                    className="px-3 py-1.5 bg-rose-600 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:bg-rose-700"
                  >
                    {downloading === `${previewReport.id}-pdf` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export PDF
                  </button>
                  <button
                    onClick={() => { setPreviewReport(null); setPreviewData(null); }}
                    className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-300"
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Saved Views Toolbar */}
              <div className="bg-slate-100 px-6 py-2 border-b border-slate-200 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-600">Saved Views:</span>
                  {savedViews.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => applySavedView(v)}
                      className="px-2.5 py-1 bg-white border border-slate-300 hover:border-blue-500 rounded text-slate-700 font-medium"
                    >
                      {v.view_name} {v.is_shared ? '(Shared)' : ''}
                    </button>
                  ))}
                  <button
                    onClick={resetToStandardView}
                    className="px-2 py-1 text-slate-500 hover:text-slate-800 underline font-medium"
                  >
                    Reset to Standard Layout
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="New view name..."
                    value={viewNameInput}
                    onChange={(e) => setViewNameInput(e.target.value)}
                    className="px-2 py-1 border border-slate-300 rounded text-xs"
                  />
                  <button
                    onClick={handleSaveView}
                    className="px-2.5 py-1 bg-blue-600 text-white rounded font-semibold flex items-center gap-1 hover:bg-blue-700"
                  >
                    <Save className="w-3 h-3" /> Save View
                  </button>
                </div>
              </div>

              {/* Column Customizer Collapsible */}
              {showCustomizer && (
                <div className="bg-slate-50 p-4 px-6 border-b border-slate-200 space-y-4" data-testid="two-panel-column-chooser">
                  {/* Action Bar Header */}
                  <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex items-center gap-2">
                      <SlidersHorizontal className="w-4 h-4 text-blue-600" />
                      <span className="font-bold text-xs text-slate-800 uppercase tracking-wider">Two-Panel Column Chooser</span>
                      <span className="text-xs text-slate-500 font-mono">
                        ({activeColumns.length} selected of {availableUnselectedKeys.length + activeColumns.length} available)
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={handleSelectAllColumns}
                        className="px-2.5 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded font-semibold border border-blue-200 transition-colors"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={handleClearAllColumns}
                        className="px-2.5 py-1 bg-rose-50 text-rose-700 hover:bg-rose-100 rounded font-semibold border border-rose-200 transition-colors"
                      >
                        Clear Optional
                      </button>
                      <button
                        type="button"
                        onClick={resetToStandardView}
                        className="px-2.5 py-1 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded font-semibold border border-slate-300 transition-colors"
                      >
                        Reset Standard
                      </button>
                    </div>
                  </div>

                  {/* Two Panels Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* LEFT PANEL: Available Columns (Unselected) */}
                    <div className="bg-white rounded-xl border border-slate-200 flex flex-col h-80 overflow-hidden shadow-sm">
                      <div className="p-3 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
                        <h5 className="font-bold text-xs text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                          Available Columns (Unselected)
                          <span className="ml-1.5 px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full text-[10px] font-mono">
                            {filteredAvailable.length}
                          </span>
                        </h5>
                      </div>

                      <div className="p-2 border-b border-slate-100 bg-slate-50">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Search available columns..."
                            value={searchAvailable}
                            onChange={(e) => setSearchAvailable(e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 bg-white"
                          />
                        </div>
                      </div>

                      <div className="overflow-y-auto flex-1 p-2 space-y-1">
                        {filteredAvailable.length === 0 ? (
                          <div className="p-4 text-center text-slate-400 text-xs italic">
                            {searchAvailable ? 'No matching available columns.' : 'All columns selected!'}
                          </div>
                        ) : (
                          filteredAvailable.map((colKey) => {
                            const field = (fieldRegistry || {})[colKey] || {};
                            const label = field.label || colKey.replace(/_/g, ' ').toUpperCase();
                            return (
                              <div
                                key={colKey}
                                onClick={() => addColumnToSelected(colKey)}
                                className="flex items-center justify-between p-2 rounded-lg hover:bg-blue-50/70 border border-transparent hover:border-blue-200 transition-colors cursor-pointer group"
                              >
                                <div className="min-w-0 pr-2">
                                  <span className="text-xs font-semibold text-slate-800 group-hover:text-blue-700 block truncate">{label}</span>
                                  <span className="text-[10px] text-slate-400 block font-mono font-normal">{colKey}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); addColumnToSelected(colKey); }}
                                  className="px-2 py-1 bg-blue-600 text-white rounded text-[11px] font-bold opacity-80 group-hover:opacity-100 flex items-center gap-1 shrink-0"
                                >
                                  Add <ArrowRight className="w-3 h-3" />
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* RIGHT PANEL: Selected Columns (Included in Report) */}
                    <div className="bg-white rounded-xl border border-blue-200 flex flex-col h-80 overflow-hidden shadow-sm">
                      <div className="p-3 bg-blue-50/70 border-b border-blue-200 flex items-center justify-between">
                        <h5 className="font-bold text-xs text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                          Selected Columns (Included in Report)
                          <span className="ml-1.5 px-2 py-0.5 bg-blue-200 text-blue-800 rounded-full text-[10px] font-mono font-bold">
                            {activeColumns.length}
                          </span>
                        </h5>
                      </div>

                      <div className="p-2 border-b border-slate-100 bg-slate-50">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Search selected columns..."
                            value={searchSelected}
                            onChange={(e) => setSearchSelected(e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 bg-white"
                          />
                        </div>
                      </div>

                      <div className="overflow-y-auto flex-1 p-2 space-y-1.5">
                        {filteredSelected.length === 0 ? (
                          <div className="p-4 text-center text-slate-400 text-xs italic">
                            No columns selected. Click "Add" from Left Panel to select columns.
                          </div>
                        ) : (
                          filteredSelected.map((colKey) => {
                            const actualIndex = activeColumns.indexOf(colKey);
                            const field = (fieldRegistry || {})[colKey] || {};
                            const isReq = REQUIRED_COLUMNS.includes(colKey);
                            const aliasVal = columnAliases[colKey] || '';
                            const defaultLabel = field.label || colKey.replace(/_/g, ' ').toUpperCase();

                            return (
                              <div
                                key={colKey}
                                draggable={!searchSelected}
                                onDragStart={() => setDraggedIndex(actualIndex)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => handleDrop(actualIndex)}
                                className="flex items-center justify-between p-2 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <GripVertical className="w-3.5 h-3.5 text-slate-400 cursor-grab shrink-0" />
                                  <span className="px-1.5 py-0.5 bg-slate-200 text-slate-700 rounded font-mono text-[10px] font-bold">
                                    #{actualIndex + 1}
                                  </span>
                                  
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-xs font-bold text-slate-800 truncate">{aliasVal || defaultLabel}</span>
                                      {isReq && (
                                        <span className="px-1.5 py-0.2 bg-amber-100 text-amber-800 border border-amber-300 rounded text-[9px] font-extrabold">
                                          Required
                                        </span>
                                      )}
                                    </div>
                                    <input
                                      type="text"
                                      placeholder={`Alias (default: ${defaultLabel})`}
                                      value={aliasVal}
                                      onChange={(e) => setColumnAliases({ ...columnAliases, [colKey]: e.target.value })}
                                      className="mt-0.5 w-full text-[10px] px-1.5 py-0.5 border border-slate-300 rounded bg-white font-mono text-slate-700 focus:ring-1 focus:ring-blue-500"
                                    />
                                  </div>
                                </div>

                                <div className="flex items-center gap-1 ml-2 shrink-0">
                                  <button
                                    type="button"
                                    disabled={actualIndex === 0 || !!searchSelected}
                                    onClick={() => moveColumn(actualIndex, actualIndex - 1)}
                                    className="p-1 hover:bg-slate-200 rounded text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent"
                                    title="Move Up"
                                  >
                                    <ArrowUp className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={actualIndex === activeColumns.length - 1 || !!searchSelected}
                                    onClick={() => moveColumn(actualIndex, actualIndex + 1)}
                                    className="p-1 hover:bg-slate-200 rounded text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent"
                                    title="Move Down"
                                  >
                                    <ArrowDown className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={isReq}
                                    onClick={() => removeColumnFromSelected(colKey)}
                                    className="p-1 hover:bg-rose-100 text-rose-600 rounded disabled:opacity-30 disabled:hover:bg-transparent ml-1"
                                    title={isReq ? "Required Column" : "Remove Column"}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Content Area */}
              <div className="overflow-auto flex-1 p-6 space-y-6">
                {/* Report Header Metadata Banner */}
                {previewData && !previewData.error && (
                  <div className="bg-slate-900 text-white p-5 rounded-xl border border-slate-800 shadow-md space-y-3" data-testid="report-preview-header font-sans">
                    <div className="flex justify-between items-start border-b border-slate-800 pb-3">
                      <div>
                        <span className="text-[10px] uppercase font-extrabold text-blue-400 tracking-widest block">HECS PayrollPro</span>
                        <h3 className="text-xl font-black tracking-tight text-white mt-0.5">
                          {(previewData.benefit_type_label ? previewData.benefit_type_label.toUpperCase() : previewReport?.name?.toUpperCase() || 'REPORT')} REPORT
                        </h3>
                      </div>
                      <span className="px-2.5 py-1 bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded font-mono text-xs font-bold">
                        {previewData.frequency || frequencyFilter?.toUpperCase() || 'REPORT'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <span className="text-slate-400 block text-[11px]">Company:</span>
                        <strong className="text-slate-100 font-semibold">{previewData.company_name || selectedCompany}</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[11px]">Branch:</span>
                        <strong className="text-slate-100 font-semibold">{previewData.branch_name || selectedBranch}</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[11px]">Benefit:</span>
                        <strong className="text-slate-100 font-semibold">{previewData.benefit_type_label || 'Statutory Benefit'}</strong>
                      </div>
                      <div>
                        <span className="text-slate-400 block text-[11px]">Reporting Period:</span>
                        <strong className="text-emerald-400 font-bold">{previewData.reporting_period || previewData.period_metadata?.display_period || period || 'N/A'}</strong>
                      </div>
                      {previewData.financial_year && (
                        <div>
                          <span className="text-slate-400 block text-[11px]">Financial Year:</span>
                          <strong className="text-purple-300 font-semibold">{previewData.financial_year}</strong>
                        </div>
                      )}
                      {previewData.payroll_period && (
                        <div>
                          <span className="text-slate-400 block text-[11px]">Payroll Period:</span>
                          <strong className="text-blue-300 font-semibold">{previewData.payroll_period}</strong>
                        </div>
                      )}
                      <div>
                        <span className="text-slate-400 block text-[11px]">Generated On:</span>
                        <strong className="text-slate-300 font-semibold">{previewData.generated_on || new Date().toLocaleDateString('en-GB')}</strong>
                      </div>
                    </div>
                  </div>
                )}

                {/* Comparison & Variance View */}
                {comparisonData && (
                  <div className="space-y-6">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <h3 className="font-bold text-amber-900 text-sm mb-1 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" /> Multi-Period Side-by-Side Comparison
                      </h3>
                      <p className="text-xs text-amber-700">Variance and % changes calculated in reporting layer without modifying source data.</p>
                    </div>

                    <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden">
                      <thead className="bg-slate-900 text-white uppercase text-[10px]">
                        <tr>
                          <th className="p-2.5 text-left">Metric</th>
                          <th className="p-2.5 text-right">{comparisonData.period_labels?.[0] || comparisonData.periods?.[0] || 'Period 1'}</th>
                          <th className="p-2.5 text-right">{comparisonData.period_labels?.[1] || comparisonData.periods?.[1] || 'Period 2'}</th>
                          <th className="p-2.5 text-right">Absolute Variance</th>
                          <th className="p-2.5 text-right">% Variance</th>
                          <th className="p-2.5 text-center">Anomaly Flag</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {comparisonData.comparison_rows.map((r, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="p-2.5 font-bold text-slate-800">{r.metric}</td>
                            <td className="p-2.5 text-right font-mono">
                              {typeof r.period_1_val === 'number' ? `₹${r.period_1_val.toLocaleString('en-IN')}` : (r.period_1_val ?? 'N/A')}
                            </td>
                            <td className="p-2.5 text-right font-mono">
                              {typeof r.period_2_val === 'number' ? `₹${r.period_2_val.toLocaleString('en-IN')}` : (r.period_2_val ?? 'N/A')}
                            </td>
                            <td className={`p-2.5 text-right font-mono font-bold ${typeof r.absolute_variance === 'number' ? (r.absolute_variance >= 0 ? 'text-emerald-600' : 'text-rose-600') : 'text-slate-600'}`}>
                              {typeof r.absolute_variance === 'number'
                                ? (r.absolute_variance >= 0 ? `+₹${r.absolute_variance.toLocaleString('en-IN')}` : `₹${r.absolute_variance.toLocaleString('en-IN')}`)
                                : (r.absolute_variance ?? 'N/A')}
                            </td>
                            <td className="p-2.5 text-right font-mono font-bold">
                              {r.pct_variance !== null && r.pct_variance !== undefined && r.pct_variance !== 'N/A'
                                ? `${Number(r.pct_variance) >= 0 ? '+' : ''}${r.pct_variance}%`
                                : <span className="text-slate-400">N/A</span>}
                            </td>
                            <td className="p-2.5 text-center">
                              {r.anomaly_flag ? (
                                <span className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full font-bold text-[10px]">
                                  ⚠️ High Variance (&gt;10%)
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-bold text-[10px]">
                                  Normal
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Waterfall Variance Bridge */}
                    {comparisonData.waterfall_bridge && comparisonData.waterfall_bridge.length > 0 && (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                        <h4 className="font-bold text-xs text-slate-800 uppercase tracking-wider mb-3">Waterfall Variance Bridge</h4>
                        <div className="flex gap-3">
                          {comparisonData.waterfall_bridge.map((b, i) => (
                            <div key={i} className="flex-1 bg-white border border-slate-200 p-3 rounded-lg text-center">
                              <span className="text-[10px] text-slate-500 uppercase block font-semibold">{b.stage}</span>
                              <span className="text-sm font-bold font-mono text-slate-900 block mt-1">₹{b.amount?.toLocaleString('en-IN')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Legacy vs Regular Payroll Comparison Summary View */}
                {previewReport?.id === 'legacy_vs_regular_comparison' && previewData?.summary && (
                  <div className="space-y-4" data-testid="legacy-vs-regular-summary">
                    {/* Match Status Pills */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h4 className="font-bold text-xs text-slate-800 uppercase tracking-wider">Payroll Matching &amp; Reconciliation Status</h4>
                        <p className="text-xs text-slate-500">Matching via stable employee identifiers across legacy import lines and regular payroll records.</p>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs font-bold">
                        <span className="px-3 py-1 bg-slate-100 text-slate-700 border border-slate-300 rounded-full">
                          Total Unique: <span className="font-mono">{previewData.summary.total_unique_employees}</span>
                        </span>
                        <span className="px-3 py-1 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-full">
                          Matched: <span className="font-mono">{previewData.summary.matched_count}</span>
                        </span>
                        <span className="px-3 py-1 bg-amber-100 text-amber-800 border border-amber-300 rounded-full">
                          Legacy Only: <span className="font-mono">{previewData.summary.legacy_only_count}</span>
                        </span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 border border-blue-300 rounded-full">
                          Regular Only: <span className="font-mono">{previewData.summary.regular_only_count}</span>
                        </span>
                        {previewData.summary.requires_review_count > 0 && (
                          <span className="px-3 py-1 bg-rose-100 text-rose-800 border border-rose-300 rounded-full">
                            Requires Review: <span className="font-mono">{previewData.summary.requires_review_count}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Metric Comparison KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                        <span className="text-[10px] uppercase font-bold text-slate-500 block">Gross Earnings</span>
                        <div className="text-xs text-slate-600 mt-1 flex justify-between">
                          <span>Legacy: ₹{previewData.summary.legacy_gross?.toLocaleString('en-IN')}</span>
                          <span>Regular: ₹{previewData.summary.regular_gross?.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-slate-500">Variance:</span>
                          <span className={`text-xs font-mono font-bold ${previewData.summary.gross_variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {previewData.summary.gross_variance >= 0 ? `+₹${previewData.summary.gross_variance?.toLocaleString('en-IN')}` : `₹${previewData.summary.gross_variance?.toLocaleString('en-IN')}`}
                            {previewData.summary.gross_pct_variance !== null && previewData.summary.gross_pct_variance !== undefined && ` (${previewData.summary.gross_pct_variance}%)`}
                          </span>
                        </div>
                      </div>

                      <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                        <span className="text-[10px] uppercase font-bold text-slate-500 block">Total Deductions</span>
                        <div className="text-xs text-slate-600 mt-1 flex justify-between">
                          <span>Legacy: ₹{previewData.summary.legacy_deductions?.toLocaleString('en-IN')}</span>
                          <span>Regular: ₹{previewData.summary.regular_deductions?.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-slate-500">Variance:</span>
                          <span className={`text-xs font-mono font-bold ${previewData.summary.deductions_variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {previewData.summary.deductions_variance >= 0 ? `+₹${previewData.summary.deductions_variance?.toLocaleString('en-IN')}` : `₹${previewData.summary.deductions_variance?.toLocaleString('en-IN')}`}
                            {previewData.summary.deductions_pct_variance !== null && previewData.summary.deductions_pct_variance !== undefined && ` (${previewData.summary.deductions_pct_variance}%)`}
                          </span>
                        </div>
                      </div>

                      <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                        <span className="text-[10px] uppercase font-bold text-slate-500 block">Net Pay</span>
                        <div className="text-xs text-slate-600 mt-1 flex justify-between">
                          <span>Legacy: ₹{previewData.summary.legacy_net?.toLocaleString('en-IN')}</span>
                          <span>Regular: ₹{previewData.summary.regular_net?.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-slate-500">Variance:</span>
                          <span className={`text-xs font-mono font-bold ${previewData.summary.net_variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {previewData.summary.net_variance >= 0 ? `+₹${previewData.summary.net_variance?.toLocaleString('en-IN')}` : `₹${previewData.summary.net_variance?.toLocaleString('en-IN')}`}
                            {previewData.summary.net_pct_variance !== null && previewData.summary.net_pct_variance !== undefined && ` (${previewData.summary.net_pct_variance}%)`}
                          </span>
                        </div>
                      </div>

                      <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                        <span className="text-[10px] uppercase font-bold text-slate-500 block">Employer PF</span>
                        <div className="text-xs text-slate-600 mt-1 flex justify-between">
                          <span>Legacy: ₹{previewData.summary.legacy_pf?.toLocaleString('en-IN')}</span>
                          <span>Regular: ₹{previewData.summary.regular_pf?.toLocaleString('en-IN')}</span>
                        </div>
                        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-[11px] font-semibold text-slate-500">Variance:</span>
                          <span className={`text-xs font-mono font-bold ${previewData.summary.pf_variance >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {previewData.summary.pf_variance >= 0 ? `+₹${previewData.summary.pf_variance?.toLocaleString('en-IN')}` : `₹${previewData.summary.pf_variance?.toLocaleString('en-IN')}`}
                            {previewData.summary.pf_pct_variance !== null && previewData.summary.pf_pct_variance !== undefined && ` (${previewData.summary.pf_pct_variance}%)`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reconciliation View */}
                {reconcileData && (
                  <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
                    <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                      <h3 className="font-bold text-slate-900 text-base">Payroll Header vs Detail Reconciliation</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-extrabold ${reconcileData.reconciliation_status === 'Reconciled' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {reconcileData.reconciliation_status}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <span className="text-xs text-slate-500 font-medium">Source Run Total Gross</span>
                        <span className="text-lg font-bold font-mono text-slate-900 block mt-1">₹{reconcileData.source_total_gross?.toLocaleString()}</span>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <span className="text-xs text-slate-500 font-medium">Detail Sum Gross</span>
                        <span className="text-lg font-bold font-mono text-slate-900 block mt-1">₹{reconcileData.detail_total_gross?.toLocaleString()}</span>
                      </div>
                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                        <span className="text-xs text-slate-500 font-medium">Variance</span>
                        <span className="text-lg font-bold font-mono text-emerald-600 block mt-1">₹{reconcileData.gross_variance}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Statutory Benefit Summary Cards */}
                {previewData?.summary && !comparisonData && !reconcileData && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200" data-testid="statutory-summary-cards">
                    <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                      <span className="text-[10px] uppercase font-bold text-slate-500 block">Total Employees</span>
                      <span className="text-lg font-extrabold text-slate-900 font-mono mt-0.5 block">{previewData.summary.total_employees}</span>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                      <span className="text-[10px] uppercase font-bold text-emerald-600 block">Eligible / Non-Eligible</span>
                      <span className="text-lg font-extrabold text-slate-900 font-mono mt-0.5 block">{previewData.summary.eligible_employees} <span className="text-xs font-medium text-slate-400">/ {previewData.summary.non_eligible_employees}</span></span>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                      <span className="text-[10px] uppercase font-bold text-blue-600 block">Monthly / Annual</span>
                      <span className="text-lg font-extrabold text-slate-900 font-mono mt-0.5 block">{previewData.summary.monthly_employees} <span className="text-xs font-medium text-slate-400">/ {previewData.summary.annual_employees}</span></span>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                      <span className="text-[10px] uppercase font-bold text-slate-500 block">Calculated Amount</span>
                      <span className="text-lg font-extrabold text-slate-900 font-mono mt-0.5 block">₹{previewData.summary.total_calculated_amount?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                      <span className="text-[10px] uppercase font-bold text-amber-600 block">Manual Adjustment</span>
                      <span className="text-lg font-extrabold text-slate-900 font-mono mt-0.5 block">₹{previewData.summary.total_manual_adjustment?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-emerald-200 shadow-sm col-span-2 md:col-span-3 bg-emerald-50/30">
                      <span className="text-[10px] uppercase font-bold text-emerald-700 block">Total Final Amount</span>
                      <span className="text-xl font-black text-emerald-700 font-mono mt-0.5 block">₹{previewData.summary.total_final_amount?.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                )}

                {/* Empty Result Handling */}
                {previewData && previewData.count === 0 && (
                  <div className="p-12 text-center bg-slate-50 border border-slate-200 rounded-xl" data-testid="no-records-banner">
                    <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <h4 className="font-bold text-slate-800 text-sm">No records found for the selected filters.</h4>
                    <p className="text-xs text-slate-500 mt-1">Try changing company, branch, frequency, or period criteria.</p>
                  </div>
                )}

                {/* Standard Data Table */}
                {previewData?.data && previewData.data.length > 0 && !comparisonData && !reconcileData && (
                  <div className="overflow-x-auto border border-slate-200 rounded-xl">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-900 text-white text-[10px] uppercase font-bold">
                        <tr>
                          {activeColumns.map((colKey) => {
                            const field = (fieldRegistry || {})[colKey] || {};
                            const title = columnAliases[colKey] || field.label || colKey.replace('_', ' ').toUpperCase();
                            return (
                              <th key={colKey} className="p-3 text-left border-r border-slate-800">
                                {title}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {previewData.data.map((row, idx) => (
                          <tr
                            key={idx}
                            onClick={() => handleDrilldown(row.department)}
                            className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                          >
                            {activeColumns.map((colKey) => {
                              const val = row[colKey];
                              const field = (fieldRegistry || {})[colKey] || {};
                              const isCurr = field.type === 'currency' || colKey.includes('_gross') || colKey.includes('_net') || colKey.includes('_deductions') || colKey.includes('_pf') || colKey.endsWith('_variance');

                              if (colKey === 'match_status') {
                                const badgeColor =
                                  val === 'Matched' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                                  val === 'Legacy Only' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                                  val === 'Regular Only' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                  'bg-rose-100 text-rose-800 border-rose-300';
                                return (
                                  <td key={colKey} className="p-2.5 text-center">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${badgeColor}`}>
                                      {val || 'Unknown'}
                                    </span>
                                    {row.review_reason && val === 'Requires Review' && (
                                      <span className="block text-[9px] text-rose-600 font-mono mt-0.5">{row.review_reason}</span>
                                    )}
                                  </td>
                                );
                              }

                              if (colKey.endsWith('_pct_variance') || colKey === 'pct_variance') {
                                return (
                                  <td key={colKey} className="p-2.5 text-right font-mono font-bold">
                                    {val === null || val === undefined || val === 'N/A' ? (
                                      <span className="text-slate-400">N/A</span>
                                    ) : (
                                      <span className={Number(val) > 0 ? 'text-emerald-600' : Number(val) < 0 ? 'text-rose-600' : 'text-slate-600'}>
                                        {Number(val) > 0 ? `+${val}%` : `${val}%`}
                                      </span>
                                    )}
                                  </td>
                                );
                              }

                              if (val === null || val === undefined || val === 'N/A') {
                                return (
                                  <td key={colKey} className="p-2.5 text-center font-mono text-slate-400">
                                    {val === 'N/A' ? 'N/A' : '—'}
                                  </td>
                                );
                              }

                              return (
                                <td key={colKey} className={`p-2.5 font-mono ${isCurr ? 'text-right' : 'text-left'}`}>
                                  {isCurr && typeof val === 'number' ? `₹${val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : String(val ?? '')}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Drill-Down Modal */}
        {drilldownModal && drilldownData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col overflow-hidden">
              <div className="p-4 px-6 bg-slate-900 text-white flex justify-between items-center">
                <h3 className="font-bold text-sm">Drill-Down: {drilldownData.department} ({drilldownData.period})</h3>
                <button onClick={() => setDrilldownModal(false)} className="text-xs bg-slate-800 px-3 py-1 rounded">Close</button>
              </div>
              <div className="p-6 overflow-auto flex-1 space-y-3">
                <p className="text-xs text-slate-500 font-medium">Traceable source employee records in selected department:</p>
                <table className="w-full text-xs border border-slate-200">
                  <thead className="bg-slate-100 text-slate-700 uppercase font-bold text-[10px]">
                    <tr>
                      <th className="p-2 text-left">Code</th>
                      <th className="p-2 text-left">Employee Name</th>
                      <th className="p-2 text-right">Gross Pay</th>
                      <th className="p-2 text-right">Net Pay</th>
                      <th className="p-2 text-center">Run ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-mono">
                    {drilldownData.records.map((r, i) => (
                      <tr key={i}>
                        <td className="p-2">{r.employee_code}</td>
                        <td className="p-2">{r.employee_name}</td>
                        <td className="p-2 text-right">₹{r.gross_pay?.toLocaleString()}</td>
                        <td className="p-2 text-right">₹{r.net_pay?.toLocaleString()}</td>
                        <td className="p-2 text-center text-slate-400 text-[10px]">{r.run_id?.slice(-8)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ReportsCenter;
